export interface Limits {
	left: number;
	top: number;
	right: number;
	bottom: number;
}

export interface Placement {
	badgeX: number;
	badgeY: number;
	titleX: number;
	titleY: number;
	lineX: number;
	lineY: number;
}

interface Size {
	width: number;
	height: number;
}

interface Rect {
	left: number;
	top: number;
	width: number;
	height: number;
}

const GAPS = [30, 46];
const SHIFTS = [-36, -18, 0, 18, 36];
const TITLE_GAP = 6;

export function placeCallout(input: {
	pointX: number;
	pointY: number;
	badge: Size;
	title: Size;
	limits: Limits;
}): Placement {
	const candidates = buildCandidates(input.pointX, input.pointY, input.badge);
	let best: { score: number; placement: Placement } | null = null;

	for (const candidate of candidates) {
		const title = titleRect(candidate, input.badge, input.title);
		const anchor = anchorOnBadge(input.pointX, input.pointY, candidate, input.badge);

		const overflow =
			overflowAmount(candidate, input.limits) +
			(input.title.height > 0 ? overflowAmount(title, input.limits) : 0);
		const length = Math.hypot(anchor.x - input.pointX, anchor.y - input.pointY);
		const score = overflow === 0 ? -length : -100000 - overflow * 20 - length;

		if (best === null || score > best.score) {
			best = {
				score,
				placement: {
					badgeX: candidate.left,
					badgeY: candidate.top,
					titleX: title.left,
					titleY: title.top,
					lineX: anchor.x,
					lineY: anchor.y,
				},
			};
		}
	}

	if (best !== null) {
		return best.placement;
	}

	return {
		badgeX: input.pointX - input.badge.width / 2,
		badgeY: input.pointY + 36,
		titleX: input.pointX - input.title.width / 2,
		titleY: input.pointY + 36 + input.badge.height + TITLE_GAP,
		lineX: input.pointX,
		lineY: input.pointY + 36,
	};
}

export function intersectLimits(a: Limits, b: Limits): Limits {
	return {
		left: Math.max(a.left, b.left),
		top: Math.max(a.top, b.top),
		right: Math.min(a.right, b.right),
		bottom: Math.min(a.bottom, b.bottom),
	};
}

export function insetLimits(limits: Limits, amount: number): Limits {
	return {
		left: limits.left + amount,
		top: limits.top + amount,
		right: limits.right - amount,
		bottom: limits.bottom - amount,
	};
}

function buildCandidates(pointX: number, pointY: number, badge: Size): Rect[] {
	const rects: Rect[] = [];
	for (const gap of GAPS) {
		for (const shift of SHIFTS) {
			rects.push({
				left: pointX - badge.width / 2 + shift,
				top: pointY + gap,
				width: badge.width,
				height: badge.height,
			});
			rects.push({
				left: pointX - badge.width / 2 + shift,
				top: pointY - gap - badge.height,
				width: badge.width,
				height: badge.height,
			});
			rects.push({
				left: pointX + gap,
				top: pointY - badge.height / 2 + shift,
				width: badge.width,
				height: badge.height,
			});
			rects.push({
				left: pointX - gap - badge.width,
				top: pointY - badge.height / 2 + shift,
				width: badge.width,
				height: badge.height,
			});
		}
	}
	return rects;
}

function titleRect(badge: Rect, badgeSize: Size, title: Size): Rect {
	if (title.height <= 0 || title.width <= 0) {
		return { left: badge.left, top: badge.top + badgeSize.height, width: 0, height: 0 };
	}
	return {
		left: badge.left + (badgeSize.width - title.width) / 2,
		top: badge.top + badgeSize.height + TITLE_GAP,
		width: title.width,
		height: title.height,
	};
}

function anchorOnBadge(pointX: number, pointY: number, badge: Rect, badgeSize: Size): { x: number; y: number } {
	const right = badge.left + badgeSize.width;
	const bottom = badge.top + badgeSize.height;
	return {
		x: clamp(pointX, badge.left, right),
		y: clamp(pointY, badge.top, bottom),
	};
}

function overflowAmount(rect: Rect, limits: Limits): number {
	if (rect.width <= 0 || rect.height <= 0) {
		return 0;
	}
	const right = rect.left + rect.width;
	const bottom = rect.top + rect.height;
	return (
		Math.max(0, limits.left - rect.left) +
		Math.max(0, limits.top - rect.top) +
		Math.max(0, right - limits.right) +
		Math.max(0, bottom - limits.bottom)
	);
}

function clamp(value: number, min: number, max: number): number {
	return Math.min(max, Math.max(min, value));
}
